/**
 * PDF Viewer Component
 *
 * Displays PDF with:
 * - Zoom controls
 * - Page navigation
 * - Source highlighting (when fields are hovered)
 * - Matches desktop PDF viewer functionality
 */

import { Box, Typography, IconButton, Paper, Alert } from '@mui/material';
import {
  ZoomIn,
  ZoomOut,
  Highlight,
} from '@mui/icons-material';
import { usePDFStore } from '../../store/pdfStore';

export const PDFViewer: React.FC = () => {
  const { currentPDF, pdfUrl, highlightBbox, extractionResults } = usePDFStore();

  // Find field name from bbox (for display purposes)
  const getFieldNameFromBbox = (bbox: [number, number, number, number] | null): string | null => {
    if (!bbox) return null;

    for (const [fieldName, fieldData] of Object.entries(extractionResults)) {
      const loc = fieldData.source_location;
      if (loc && loc[0] === bbox[0] && loc[1] === bbox[1] && loc[2] === bbox[2] && loc[3] === bbox[3]) {
        return fieldName;
      }
    }
    return null;
  };

  const highlightedField = getFieldNameFromBbox(highlightBbox);

  if (!currentPDF) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No PDF loaded. Upload a PDF to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* PDF Controls */}
      <Paper
        elevation={0}
        sx={{
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {currentPDF.file_name}
        </Typography>

        <Box>
          <IconButton size="small">
            <ZoomOut fontSize="small" />
          </IconButton>
          <IconButton size="small">
            <ZoomIn fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      {/* PDF Display */}
      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#525659', position: 'relative' }}>
        {/* Highlight indicator overlay */}
        {highlightedField && (
          <Alert
            icon={<Highlight />}
            severity="warning"
            sx={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              minWidth: 250,
              boxShadow: 3,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Source: {highlightedField}
            </Typography>
            <Typography variant="caption">
              Bbox: [{highlightBbox?.join(', ')}]
            </Typography>
          </Alert>
        )}

        {pdfUrl ? (
          <iframe
            src={pdfUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            title="PDF Viewer"
          />
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <Typography>Loading PDF...</Typography>
          </Box>
        )}
      </Box>

    </Box>
  );
};
